import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserCircleIcon, ArrowLeftOnRectangleIcon, MagnifyingGlassIcon, Bars3Icon, XMarkIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
const logo = '/logo-vert.png'; // Correct way to reference images in public folder
import SearchBar from '../Common/SearchBar'; 
import { useAuth } from '../../contexts/AuthContext'; // Corrected useAuth import
import api from '../../services/api'; // Import api for base URL

// Helper function to get user initials
const getUserInitials = (name) => {
  if (!name) return '';
  const nameParts = name.split(' ');
  if (nameParts.length > 1 && nameParts[0] && nameParts[nameParts.length - 1]) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }
  if (nameParts[0] && nameParts[0][0]) {
    return nameParts[0][0].toUpperCase();
  }
  return '';
};

const Header = () => {
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [searchQuery, setSearchQuery] = useState('');
  const [userName, setUserName] = useState('');

  const { user, loading: authLoading } = useAuth();

  // userName state can still be used for other purposes if needed, 
  // but for avatar display, we'll use user prop directly.
  // The useEffect for userName might still be useful for the dropdown's detailed view.
  useEffect(() => {
    if (user) {
      setUserName(user.namaLengkap || user.email || 'User');
    } else if (!authLoading) {
      setUserName('');
    }
  }, [user, authLoading]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    // Optionally remove other user-related items from localStorage
    navigate('/login');
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    console.log("Search initiated for:", query);
    // Example: navigate(`/search?q=${query}`);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const profileButton = document.getElementById('profile-dropdown-button');
      const profileMenu = document.getElementById('profile-dropdown-menu');
      if (isProfileDropdownOpen && profileButton && !profileButton.contains(event.target) && profileMenu && !profileMenu.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  if (authLoading) {
    // Render a minimal header or a loading state if preferred
    return (
      <header className="bg-white shadow-md sticky top-0 z-40">
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>Loading...</div>
          </div>
        </nav>
      </header>
    );
  }

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-40 border-b border-gray-100">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center group">
              <img className="h-10 w-auto transition-transform duration-200 group-hover:scale-105" src={logo} alt="Logo Teraplus" />
            </Link>
          </div>

          {/* Desktop Navigation Links & Search (REMOVED AS PER REQUEST) */}
          
          {/* Mobile Menu Button & Profile Dropdown */}
          <div className="flex items-center ml-auto">
            <div className="md:hidden mr-3">
              <button
                onClick={toggleMobileMenu}
                className="text-gray-600 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 p-2 rounded-lg transition-colors duration-200"
                aria-label="Open main menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Profile Dropdown */}
            {user ? (
              <div className="relative ml-3">
                <div>
                  <button
                    id="profile-dropdown-button"
                    onClick={toggleProfileDropdown}
                    className="max-w-xs bg-gradient-to-r from-gray-50 to-blue-50 rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 p-1 hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 border border-gray-200 hover:border-blue-300"
                    aria-expanded={isProfileDropdownOpen}
                    aria-haspopup="true"
                    aria-label="User menu"
                  >
                    {user.profilePicture ? (
                      <img
                        className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                        src={user.profilePicture.startsWith('http') ? user.profilePicture : `${api.defaults.baseURL.replace('/api', '')}${user.profilePicture.startsWith('/') ? user.profilePicture : '/' + user.profilePicture}`}
                        alt="Profile"
                      />
                    ) : (
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 ring-2 ring-white">
                        <span className="text-sm font-semibold leading-none text-white">{getUserInitials(user.namaLengkap || user.email)}</span>
                      </span>
                    )}
                    <span className="hidden md:block ml-2 text-sm font-medium text-gray-700">{user.namaLengkap || user.email || ''}</span>
                  </button>
                </div>
                {isProfileDropdownOpen && (
                  <div
                    id="profile-dropdown-menu"
                    className="origin-top-right absolute right-0 mt-3 w-64 rounded-xl shadow-xl py-2 bg-white ring-1 ring-gray-200 focus:outline-none z-50 border border-gray-100"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="profile-dropdown-button"
                  >
                    <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">
                      <p className="font-medium text-gray-500">Signed in as</p>
                      <p className="truncate font-semibold text-gray-900">{userName}</p> 
                      {user.role && <p className="text-xs text-blue-600 capitalize font-medium">({user.role})</p>}
                    </div>
                    <Link to="/profile" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 w-full text-left transition-colors duration-150" role="menuitem">
                      <UserCircleIcon className="h-5 w-5 mr-3 text-gray-500" />
                      Profile
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/admin" className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 w-full text-left transition-colors duration-150" role="menuitem">
                        <Cog8ToothIcon className="h-5 w-5 mr-3 text-gray-500" />
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-red-50 w-full text-left transition-colors duration-150"
                      role="menuitem"
                    >
                      <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-3 text-gray-500" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-3">
                <Link to="/login" className="text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">Login</Link>
                <Link to="/register" className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg">Register</Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'} absolute top-16 inset-x-0 bg-white/95 backdrop-blur-sm shadow-xl z-30 rounded-b-xl border border-gray-100 mx-4`}>
          <div className="px-4 pt-4 pb-6 space-y-3">
            <SearchBar onSearch={handleSearch} placeholder="Cari kursus..." />
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 hover:bg-blue-50 block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Home</Link>
            {user && user.role === 'admin' && (
              <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 hover:bg-blue-50 block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Admin Panel</Link>
            )}
            {user ? (
              <>
                <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 hover:bg-blue-50 block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Profile</Link>
                <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="text-gray-700 hover:bg-red-50 block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 hover:bg-blue-50 block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Login</Link>
                <Link to="/register" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-700 hover:bg-blue-50 block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-150">Register</Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
